import { useCallback, useState } from "react"
import { resolveAsFunction } from "lib/resolve-as-function"
import { noChange, useRefresh } from "lib/@hooks/useRefresh"
import { Bound } from "lib/@components/binding/Bound"
import { OkCancel } from "lib/@components/okCancel"
import { useYupContext } from "lib/@components/binding/use-yup-wrapper"
import { debounce } from "lib/debounce"
import { cloneDeep } from "lib/clone"

/**
 * Dialog body that binds its children to a cloned target and gates the OK button on yup validation.
 *
 * @param {Object} params
 * @param {Object|Function} [params.initialValue={}] - Initial target (or factory returning one);
 *     it is deep-cloned so edits never mutate the caller's object until OK resolves.
 * @param {ReactNode} params.children - Bound input components editing the target.
 * @param {boolean} [params.startValid=false] - When true, the OK button is enabled initially —
 *     use this for pre-populated/edit dialogs whose initial value is already valid. The default
 *     (false) keeps OK disabled until the user's input passes validation via the onChange chain.
 */
export function DataInputDialog({ initialValue = {}, children, startValid, ...props }) {
    const [target] = useState(() => cloneDeep(resolveAsFunction(initialValue)()))
    const [yupContext, validate] = useYupContext()
    const refresh = useRefresh(noChange)
    const [disabled, setDisabled] = useState(!startValid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const checkContext = useCallback(debounce(_checkContext, 100), [validate])
    return (
        <Bound onChange={checkContext} target={target} refresh={refresh} yupContext={yupContext}>
            {children}
            <OkCancel
                {...props}
                okDisabled={checkDisabled}
                value={Object.values(target).filter(Boolean).length ? target : undefined}
            >
                {props.okChildren}
            </OkCancel>
        </Bound>
    )

    function checkDisabled(...params) {
        if (props.okDisabled) {
            if (props.okDisabled(...params)) return true
        }
        return disabled
    }

    function _checkContext() {
        setDisabled(!validate())
        return false
    }
}
